import { api } from "_shared/_stores/api";
import { permissionCacheTags } from "permissions/_api/permission-cache-tags";
import { z } from "zod";

/**
 * The permission model's vocabulary, from `GET /permissions/kinds`: every area, and every kind of
 * grant with its business name, the "may …" sentence, its route slug, the noun for a per-record
 * kind's target, what a one-click Grant sends, and which controls a grant row draws.
 *
 * The server's `PermissionAreas` is the one definition. Read it from here rather than keeping a
 * vocabulary of resource types in the client; the matrix's own taxonomy drifted from it and went
 * with the matrix (#6177).
 */

const PermissionKindControlOptionZ = z.object({
  value: z.number().int(),
  name: z.string(),
  superUserOnly: z.boolean(),
});

const PermissionKindControlZ = z.object({
  name: z.enum(["accessLevel", "scope", "fields"]),
  options: z.array(PermissionKindControlOptionZ),
});

/**
 * What a standing kind's grant reaches at one scope, from the checks that ask for it (#6831): a
 * clause that completes "covers …", and false in `inEffect` where no check accepts a grant there.
 */
const PermissionKindScopeReachZ = z.object({
  scope: z.number().int(),
  covers: z.string(),
  inEffect: z.boolean(),
});

const PermissionKindZ = z.object({
  slug: z.string(),
  resourceType: z.string(),
  discriminator: z.object({ property: z.string(), value: z.number().int() }).nullable(),
  area: z.string(),
  name: z.string(),
  allows: z.string(),
  perRecord: z.boolean(),
  targetNoun: z.string().nullable(),
  targetIdProperty: z.string().nullable(),
  grantable: z.boolean(),
  retired: z.boolean(),
  superUserOnly: z.boolean(),
  default: z.object({ accessLevel: z.number().int().nullable(), scope: z.number().int().nullable() }).nullable(),
  containment: z.record(z.string(), z.union([z.string(), z.number()])),
  controls: z.array(PermissionKindControlZ),
  /** Lowest scope first, Global left out. Empty for a per-record kind and the sign-in kinds. */
  reach: z.array(PermissionKindScopeReachZ),
  /** The lowest level any check of the kind accepts: Read/Write for signing authority, else null. */
  leastEffectiveLevel: z.number().int().nullable(),
});

export const PermissionKindsZ = z.object({
  areas: z.array(z.string()),
  kinds: z.array(PermissionKindZ),
});

export type PermissionKindControlOption = z.infer<typeof PermissionKindControlOptionZ>;
export type PermissionKindControl = z.infer<typeof PermissionKindControlZ>;
export type PermissionKindScopeReach = z.infer<typeof PermissionKindScopeReachZ>;
export type PermissionKind = z.infer<typeof PermissionKindZ>;
export type PermissionKinds = z.infer<typeof PermissionKindsZ>;

/**
 * The value the permissions list's `resource` filter takes to find exactly one kind. The filter is
 * jsonb containment on the server, so the object is sent as JSON.
 */
export const resourceFilterFor = (kind: PermissionKind): string => JSON.stringify(kind.containment);

/**
 * The answer to `GET /permissions/kinds/holders`: how many people in the organization hold each
 * standing kind, keyed by slug, with 0 for a kind nobody holds. One request serves every count on the
 * permissions home and in the header search (#6312); it used to be one request per kind.
 */
export const KindHolderCountsZ = z.record(z.string(), z.number().int().nonnegative());

export type KindHolderCounts = z.infer<typeof KindHolderCountsZ>;

export const permissionKindsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPermissionKinds: build.query<PermissionKinds, void>({
      query: () => ({ url: "permissions/kinds", method: "GET" }),
      transformResponse: (response: unknown) => PermissionKindsZ.parse(response),
      // The vocabulary changes only with a deployment, so one fetch serves the session.
      keepUnusedDataFor: Number.POSITIVE_INFINITY,
    }),
    getKindHolderCounts: build.query<KindHolderCounts, void>({
      query: () => ({ url: "permissions/kinds/holders", method: "GET" }),
      transformResponse: (response: unknown) => KindHolderCountsZ.parse(response),
      // The counts are a view of the grants, so they are refetched with the permissions list: every
      // grant, change and removal invalidates this tag (`permissions-api-service.ts`). They count
      // across the organization, so a grant another administrator changed refetches them too (#6785).
      providesTags: [{ type: "Permission", id: "LIST" }, permissionCacheTags.organization],
    }),
  }),
});

export const { useGetPermissionKindsQuery, useGetKindHolderCountsQuery } = permissionKindsApi;
